import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/core/api/api.service';
import { COMPANIES_ENDPOINTS } from 'src/app/core/api/endpoints/companies.endpoints';

@Injectable({
  providedIn: 'root',
})
export class CompanyServicesService {
  private api = inject(ApiService);

  private updateMemberPayload(formValues: any) {
    return {
      user: {
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        email: formValues.email,
        phone_number: `00966${formValues.phone_number}`,
        is_test: formValues.is_test,
        is_active: formValues.is_active
      },
      groups: [formValues.user_groups],
      company: {
        id: formValues.company_id
      }
    };
  }

  private addMemberPayload(formValues: any) {
    return {
      user: {
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        email: formValues.email,
        phone_number: '00966' + formValues.phone_number,
        is_test: formValues.is_test,
        is_active: formValues.is_active
      },
      groups: [formValues.user_groups],
      company: {
        id: formValues.company_id
      }
    };
  }

  getCompaniesList(params) {
    return this.api
      .get(COMPANIES_ENDPOINTS.V1.COMPANIES, { params: params })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  addCompany(data) {
    return this.api.post(COMPANIES_ENDPOINTS.V1.COMPANIES, data).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  UpdateCompany(companyId, data) {
    return this.api
      .patch(COMPANIES_ENDPOINTS.V1.COMPANY_DETAIL(companyId), data)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getCompanyDetails(companyId) {
    return this.api
      .get(COMPANIES_ENDPOINTS.V1.COMPANY_DETAIL(companyId))
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  deleteCompany(companyId) {
    return this.api
      .delete(COMPANIES_ENDPOINTS.V1.COMPANIES, {
        body: companyId,
      })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getCompaniesTeamList(params: any): Observable<any> {
    return this.api
      .get(COMPANIES_ENDPOINTS.V1.COMPANY_TEAM_LIST, { params: params })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  addCompanyMember(data) {
    const payload = this.addMemberPayload(data);
    return this.api.post(COMPANIES_ENDPOINTS.V1.B2B_CREATE_COMPANY_USERS, payload).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  updateCompanyMember(id, data) {
    const payload = this.updateMemberPayload(data);
    return this.api.put(COMPANIES_ENDPOINTS.V1.COMPANY_TEAM_DETAIL(id), payload).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  bulkAddCompanyMember(data) {
    return this.api.post(COMPANIES_ENDPOINTS.V1.B2B_BULK_CREATE_COMPANY_USERS, data)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getBulkTemplate(): Observable<any> {
    return this.api.get(COMPANIES_ENDPOINTS.V1.B2B_DOWNLOAD_COMPANY_USERS_TEMPLATE, { responseType: 'blob' });
  }

  deleteBulkCompanyMember(memberIds: any[]) {
    return this.api.delete(COMPANIES_ENDPOINTS.V1.COMPANY_TEAM_LIST, {
      body: memberIds
    });
  }

  deleteSingleCompanyMember(memberId): Observable<any> {
    return this.api.delete(COMPANIES_ENDPOINTS.V1.COMPANY_TEAM_DETAIL(memberId));
  }

  getCompanyContracts(companyId: number, params?: any): Observable<any> {
    return this.api.get(COMPANIES_ENDPOINTS.V1.COMPANY_CONTRACTS(companyId), { params }).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getCompanyMemberDetails(companyId) {
    return this.api
      .get(COMPANIES_ENDPOINTS.V1.COMPANY_TEAM_DETAIL(companyId))
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getCompanyGroups(params?): Observable<any> {
    return this.api.get(COMPANIES_ENDPOINTS.V1.COMPANY_GROUPS, { params: params });
  }

  getCompanyGroupById(groupId: string): Observable<any> {
    return this.api.get(COMPANIES_ENDPOINTS.V1.COMPANY_GROUP_DETAIL(groupId));
  }

  getPermissions() {
    return this.api.get(COMPANIES_ENDPOINTS.V1.COMPANY_PERMISSIONS);
  }

  private preparePayload(data: any) {
    return {
      ...data,
      group_label: 'COMPANY'
    }
  }

  addCompanyGroup(data) {
    const payload = this.preparePayload(data);
    return this.api.post(COMPANIES_ENDPOINTS.V1.COMPANY_GROUPS, payload);
  }

  updateCompanyGroup(groupId, data) {
    const payload = this.preparePayload(data);
    return this.api.put(COMPANIES_ENDPOINTS.V1.COMPANY_GROUP_DETAIL(groupId), payload);
  }

  deleteCompanyGroup(groupId) {
    return this.api.delete(COMPANIES_ENDPOINTS.V1.COMPANY_GROUP_DETAIL(groupId));
  }
}
